import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useQuery } from 'hooks/useCustomQuery';
import {
    Navigate,
    useLocation,
    useNavigate,
    useParams,
} from 'react-router-dom';

import {
    LoadingIndicator,
    Alert,
    AlertIcon,
    AlertDescription,
} from '@/components/v4';

import FormBase from '@/components/forms/new/FormBase';
import BreadcrumbTrail from 'components/BreadcrumbTrail';
import { getAppAuthById } from 'API/AppAuth';
import config from '@/config.json';

// Form field components
import ZoomManagedByField from './formFields/ZoomManagedByField';
import ZoomMaxAccessFailuresField from './formFields/ZoomMaxAccessFailuresField';
import ZoomAccessFailureWindowField from './formFields/ZoomAccessFailureWindowField';
import ZoomUserSyncField from './formFields/ZoomUserSyncField';

// NOTE: 3.93.3 this page is read-only, still waiting for BE approval for updating
export function ZoomAuthEdit() {
    const { id } = useParams();
    const { state } = useLocation();
    const navigate = useNavigate();
    const [error] = useState(null);

    const { currentCompany: companyId } = useSelector((state) => ({
        ...state.navigationLists,
    }));

    const { darkMode } = useSelector((state) => state.settings);
    const { requiredScope: accountScope } = useSelector((state) => ({
        ...state.login,
    }));

    const managedByOptions = config.users.managedBy;

    const {
        data: appAuthData,
        status,
        isFetching,
    } = useQuery({
        queryKey: ['ZoomAppAuth', { id }],
        queryFn: async () => {
            const res = await getAppAuthById(id);
            return res.data;
        },
        enabled: !!id,
        refetchOnWindowFocus: false,
    });

    const handleBackButton = () => {
        navigate('/dashboard-services');
    };

    if (status === 'loading' || isFetching) {
        return <LoadingIndicator />;
    }

    if (appAuthData?.companyID !== companyId) {
        return <Navigate to="/dashboard-services" />;
    }

    // Map data for form initialization
    const initialValues = {
        name: state?.service?.serviceType || 'Zoom',
        defaultManagedBy: appAuthData?.defaultManagedBy || 0,
        maxAccessFailures: appAuthData?.maxAccessFailures || 0,
        accessFailureWindow: appAuthData?.accessFailureWindow || 0,
        userSync: appAuthData?.userSync || false,
        visibleScope: 0, // Default scope
    };

    const breadCrumbTrail = [
        { label: 'Services', path: ['/dashboard-services', { index: 0 }] },
        { label: 'View Zoom Service', path: '' },
    ];

    return (
        <>
            <BreadcrumbTrail
                breadCrumbTrail={breadCrumbTrail}
                handleBackButton={handleBackButton}
            />
            <FormBase
                header={`Viewing ${state?.service?.serviceType || 'Zoom'}`}
                subTitleHeader={true}
                initialValues={initialValues}
                finalFormSubmit={() => {}} // Read-only, no submit
                customClick={true}
                onBack={handleBackButton}
                isLoading={false}
                enableReinitialize={true}
                isReadOnly={true} // Make entire form read-only
            >
                {
                    ({ values, setFieldValue }) =>
                        [
                            <ZoomManagedByField
                                key="defaultManagedBy"
                                field={{
                                    name: 'defaultManagedBy',
                                    value: values.defaultManagedBy,
                                }}
                                form={{
                                    values,
                                    setFieldValue,
                                    errors: {},
                                    touched: {},
                                }}
                                options={managedByOptions}
                                darkMode={darkMode}
                                disabled={true}
                            />,
                            accountScope > 20 && (
                                <ZoomMaxAccessFailuresField
                                    key="maxAccessFailures"
                                    darkMode={darkMode}
                                    disabled={true}
                                />
                            ),
                            accountScope > 20 && (
                                <ZoomAccessFailureWindowField
                                    key="accessFailureWindow"
                                    darkMode={darkMode}
                                    disabled={true}
                                />
                            ),
                            <ZoomUserSyncField
                                key="userSync"
                                darkMode={darkMode}
                                disabled={true}
                            />,
                            error && (
                                <Alert
                                    key="error"
                                    status="error"
                                    sx={{ marginTop: '1rem' }}>
                                    <AlertIcon />
                                    <AlertDescription>{error}</AlertDescription>
                                </Alert>
                            ),
                        ].filter(Boolean) // Filter out false values from conditional rendering
                }
            </FormBase>
        </>
    );
}
